import type { ApiPaths, ApiSchema } from "@/modules/api";
import { NoteContentDocumentModelData, NoteModelData } from "@/store/note/types";
import { CollectionMetadataModelData, CollectionModelData } from "@/store/collections/types";
import { CollectionItemModelData } from "@/store/collection-items/types";
import { FavoriteItemModelData } from "@/store/favorite-items/types";
import {
  SpaceAccountCollectionModelData,
  SpaceAccountNoteModelData,
} from "@/store/recent-items/types";
import { SavedSearchModelData } from "@/store/saved-searches/types";
import { Uuid } from "@/domains/global/identifiers";
import { ExternalOperationKind, SerializedSyncOperation } from "@/store/sync/operations/types";
import { ContactModelData, SpaceAccountContactModelData } from "@/store/contacts/types";
import { AssertTrue, ValidateEnumIsSubtype } from "@/domains/typing/types";
import { SyncModelScope } from "@/domains/sync-scopes/types";
import { SpaceAccountAppCalloutModelData } from "@/store/app-callout/types";
import { ApiKeyModelData } from "@/store/api-keys/types";
import {
  TemplateModelData,
  TemplateContentDocumentModelData,
  SpaceAccountTemplateModelData,
} from "@/store/templates/types";
import { DataExportSyncModelData } from "@/store/data-exports/types";
import { SessionModelData, SessionEventModelData } from "@/store/sessions/types";
import { NoteSourceModelData, SourceModelData } from "@/store/sources/types";
import { UploadedFileBatchModelData, UploadedFileModelData } from "@/store/uploaded-files/types";
import { FeedbackModelData } from "@/store/feedback/types";
import { AudioRecordingModelData } from "@/store/audio-recordings/types";
import { SpaceAccountCalendarModelData } from "@/store/calendar/space-account-calendar/types";
import { SpaceAccountCalendarConnectionModelData } from "@/store/calendar/space-account-calendar-connection/types";
import { SpaceAccountConnectionModelData } from "@/store/connections/types";
import { SpaceAccountCalendarEventModelData } from "@/store/calendar/space-account-calendar-event/types";
import { SpaceAccountSettingsModelData } from "@/store/space-account-settings/types";
import { SpaceAccountNoteAttachmentModelData } from "@/store/note-attachments/types";
import type {
  AssistantConversationItemModelData,
  AssistantConversationModelData,
  AssistantFollowUpModelData,
  AssistantNoteActivityModelData,
  AssistantNoteReviewStatusModelData,
  AssistantObjectiveModelData,
  AssistantProjectActivityModelData,
  AssistantProjectLabelAssignmentModelData,
  AssistantProjectLabelGroupModelData,
  AssistantProjectLabelModelData,
  AssistantProjectModelData,
  AssistantRoutineModelData,
  AssistantSkillModelData,
  AssistantSpaceAccountSettingsModelData,
  AssistantTaskActivityModelData,
  AssistantTaskLabelAssignmentModelData,
  AssistantTaskNoteReferenceModelData,
  AssistantTaskLabelGroupModelData,
  AssistantTaskLabelModelData,
  AssistantTaskModelData,
} from "@/store/assistant/types";

export type AcknowledgedOperation<T> = T & {
  latest_space_account_sequence_id: number;
};

export type OptimisticUpdate<T> = T & {
  optimistic_update_id: Uuid;
  locally_committed_at: string;
};

export type SyncModelKindValues =
  | ApiSchema["SyncModelKind"]
  | ApiSchema["SyncPullApi__SyncModelKind"]
  | "ASSISTANT_TASK"
  | "ASSISTANT_PROJECT"
  | "ASSISTANT_TASK_ACTIVITY"
  | "ASSISTANT_PROJECT_ACTIVITY"
  | "ASSISTANT_NOTE_ACTIVITY"
  | "ASSISTANT_TASK_LABEL"
  | "ASSISTANT_TASK_LABEL_GROUP"
  | "ASSISTANT_TASK_LABEL_ASSIGNMENT"
  | "ASSISTANT_TASK_NOTE_REFERENCE"
  | "ASSISTANT_PROJECT_LABEL"
  | "ASSISTANT_PROJECT_LABEL_GROUP"
  | "ASSISTANT_PROJECT_LABEL_ASSIGNMENT"
  | "ASSISTANT_FOLLOW_UP"
  | "ASSISTANT_ROUTINE"
  | "ASSISTANT_SKILL"
  | "ASSISTANT_OBJECTIVE"
  | "ASSISTANT_NOTE_REVIEW_STATUS"
  | "ASSISTANT_SPACE_ACCOUNT_SETTINGS"
  | "ASSISTANT_CONVERSATION"
  | "ASSISTANT_CONVERSATION_ITEM";

export enum SyncModelKind {
  Note = "NOTE",
  NoteContentDocument = "NOTE_CONTENT_DOCUMENT",
  Collection = "COLLECTION",
  CollectionItem = "COLLECTION_ITEM",
  CollectionMetadata = "COLLECTION_METADATA",
  FavoriteItem = "FAVORITE_ITEM",
  SpaceAccountNote = "SPACE_ACCOUNT_NOTE",
  SpaceAccountCollection = "SPACE_ACCOUNT_COLLECTION",
  SavedSearch = "SAVED_SEARCH",
  Contact = "CONTACT",
  SpaceAccountContact = "SPACE_ACCOUNT_CONTACT",
  DataExport = "DATA_EXPORT",
  SpaceAccountAppCallout = "SPACE_ACCOUNT_APP_CALLOUT",
  ApiKey = "API_KEY",
  Template = "TEMPLATE",
  TemplateContentDocument = "TEMPLATE_CONTENT_DOCUMENT",
  SpaceAccountTemplate = "SPACE_ACCOUNT_TEMPLATE",
  Session = "SESSION",
  SessionEvent = "SESSION_EVENT",
  Source = "SOURCE",
  NoteSource = "NOTE_SOURCE",
  UploadedFile = "UPLOADED_FILE",
  UploadedFileBatch = "UPLOADED_FILE_BATCH",
  AudioRecording = "AUDIO_RECORDING",
  Feedback = "FEEDBACK",
  SpaceAccountCalendar = "SPACE_ACCOUNT_CALENDAR",
  SpaceAccountCalendarConnection = "SPACE_ACCOUNT_CALENDAR_CONNECTION",
  SpaceAccountConnection = "SPACE_ACCOUNT_CONNECTION",
  SpaceAccountCalendarEvent = "SPACE_ACCOUNT_CALENDAR_EVENT",
  SpaceAccountSettings = "SPACE_ACCOUNT_SETTINGS",
  SpaceAccountNoteAttachment = "SPACE_ACCOUNT_NOTE_ATTACHMENT",
  AssistantTask = "ASSISTANT_TASK",
  AssistantProject = "ASSISTANT_PROJECT",
  AssistantTaskActivity = "ASSISTANT_TASK_ACTIVITY",
  AssistantProjectActivity = "ASSISTANT_PROJECT_ACTIVITY",
  AssistantNoteActivity = "ASSISTANT_NOTE_ACTIVITY",
  AssistantTaskLabel = "ASSISTANT_TASK_LABEL",
  AssistantTaskLabelGroup = "ASSISTANT_TASK_LABEL_GROUP",
  AssistantTaskLabelAssignment = "ASSISTANT_TASK_LABEL_ASSIGNMENT",
  AssistantTaskNoteReference = "ASSISTANT_TASK_NOTE_REFERENCE",
  AssistantProjectLabel = "ASSISTANT_PROJECT_LABEL",
  AssistantProjectLabelGroup = "ASSISTANT_PROJECT_LABEL_GROUP",
  AssistantProjectLabelAssignment = "ASSISTANT_PROJECT_LABEL_ASSIGNMENT",
  AssistantFollowUp = "ASSISTANT_FOLLOW_UP",
  AssistantRoutine = "ASSISTANT_ROUTINE",
  AssistantSkill = "ASSISTANT_SKILL",
  AssistantObjective = "ASSISTANT_OBJECTIVE",
  AssistantNoteReviewStatus = "ASSISTANT_NOTE_REVIEW_STATUS",
  AssistantSpaceAccountSettings = "ASSISTANT_SPACE_ACCOUNT_SETTINGS",
  AssistantConversation = "ASSISTANT_CONVERSATION",
  AssistantConversationItem = "ASSISTANT_CONVERSATION_ITEM",
}

type _ValidateSyncModelKindValues = AssertTrue<
  ValidateEnumIsSubtype<SyncModelKind, SyncModelKindValues>
>;

export type ListSyncUpdatesResponse = ApiSchema["ListSyncUpdatesResponseSchema"];
export type SyncUpdateResponse = ListSyncUpdatesResponse["results"][number];

export type UpsertedSyncUpdateResponse = ApiSchema["UpsertedSyncUpdateResponseSchema"];
export type DeletedSyncUpdateResponse = ApiSchema["DeletedSyncUpdateResponseSchema"];
export type AclUpsertedSyncUpdateResponse = ApiSchema["AclUpsertedSyncUpdateResponseSchema"];
export type AclRevokedSyncUpdateResponse = ApiSchema["AclRevokedSyncUpdateResponseSchema"];

export type BaseCombinedSyncUpdate =
  | UpsertedSyncUpdateResponse
  | DeletedSyncUpdateResponse
  | AclUpsertedSyncUpdateResponse
  | AclRevokedSyncUpdateResponse;

export type SyncUpdateShared = Omit<
  BaseCombinedSyncUpdate,
  "sync_id" | "locally_committed_at" | "committed_at"
>;

export type SyncOperation = ApiSchema["SubmitSyncOperationRequest"];
export type SyncOperationKind = SyncOperation["operation_kind"];
export type AcknowledgedSyncOperation = AcknowledgedOperation<SyncOperation>;

// NOTE: This is a temporary type until the backend supports guest mode sync operations
export type GuestSyncOperation = ApiSchema["SubmitSyncOperationRequest"];
export type GuestSyncOperationKind = GuestSyncOperation["operation_kind"];
export type AcknowledgedGuestSyncOperation = AcknowledgedOperation<GuestSyncOperation>;
/**
 * @todo - Swap this to use the real `ListGuestModeSyncUpdatesResponseSchema` once it is
 * implemented on the backend.
 */
export type GuestListSyncUpdatesResponse = ApiSchema["ListSyncUpdatesResponseSchema"];

export type GenericSyncOperationKind =
  | SyncOperationKind
  | GuestSyncOperationKind
  | ExternalOperationKind
  // Hand-augmented: the generated legacy client-submit union lags the
  // SyncPushApi contract that carries the op.
  | "MARK_SESSION_SEEN"
  | "UPDATE_ASSISTANT_ROUTINE"
  | "DELETE_ASSISTANT_ROUTINE"
  | "CREATE_ASSISTANT_SKILL"
  | "UPDATE_ASSISTANT_SKILL"
  | "DELETE_ASSISTANT_SKILL";

export type SyncModelData =
  | NoteModelData
  | NoteContentDocumentModelData
  | CollectionModelData
  | CollectionItemModelData
  | SavedSearchModelData
  | FavoriteItemModelData
  | ContactModelData
  | SpaceAccountContactModelData
  | CollectionMetadataModelData
  | SpaceAccountNoteModelData
  | SpaceAccountCollectionModelData
  | DataExportSyncModelData
  | SpaceAccountAppCalloutModelData
  | ApiKeyModelData
  | TemplateModelData
  | TemplateContentDocumentModelData
  | SpaceAccountTemplateModelData
  | SessionModelData
  | SessionEventModelData
  | SourceModelData
  | NoteSourceModelData
  | UploadedFileModelData
  | UploadedFileBatchModelData
  | AudioRecordingModelData
  | FeedbackModelData
  | SpaceAccountCalendarModelData
  | SpaceAccountCalendarConnectionModelData
  | SpaceAccountConnectionModelData
  | SpaceAccountCalendarEventModelData
  | SpaceAccountSettingsModelData
  | SpaceAccountNoteAttachmentModelData
  | AssistantTaskModelData
  | AssistantProjectModelData
  | AssistantTaskActivityModelData
  | AssistantProjectActivityModelData
  | AssistantNoteActivityModelData
  | AssistantTaskLabelModelData
  | AssistantTaskLabelGroupModelData
  | AssistantTaskLabelAssignmentModelData
  | AssistantTaskNoteReferenceModelData
  | AssistantProjectLabelModelData
  | AssistantProjectLabelGroupModelData
  | AssistantProjectLabelAssignmentModelData
  | AssistantFollowUpModelData
  | AssistantRoutineModelData
  | AssistantSkillModelData
  | AssistantObjectiveModelData
  | AssistantNoteReviewStatusModelData
  | AssistantSpaceAccountSettingsModelData
  | AssistantConversationModelData
  | AssistantConversationItemModelData;

export type SyncUpdateValue<ModelData extends SyncModelData> = {
  model_id: string;
  model_kind: SyncModelKindValues;
  model_version: number;
  model_data: ModelData;
  model_scopes: SyncModelScope[];
};
export type SyncUpdatePartialValue = ApiSchema["SyncUpdatePartialValueResponseSchema"];
export type SyncModelUpsertedSyncUpdate<ModelData extends SyncModelData> = SyncUpdateShared & {
  value: SyncUpdateValue<ModelData>;
};
export type SyncModelDeletedSyncUpdate = SyncUpdateShared & {
  value: SyncUpdatePartialValue;
};
export type SyncUpdate<ModelData extends SyncModelData> =
  | SyncModelUpsertedSyncUpdate<ModelData>
  | SyncModelDeletedSyncUpdate;

export type OptimisticSyncUpdate<ModelData extends SyncModelData> = OptimisticUpdate<
  SyncUpdate<ModelData>
>;

type JsonResponse<Path extends string, Method extends string> = Path extends keyof ApiPaths
  ? Method extends keyof ApiPaths[Path]
    ? ApiPaths[Path][Method] extends {
        responses: {
          200: {
            content: {
              "application/json": infer Response;
            };
          };
        };
      }
      ? Response
      : never
    : never
  : never;

export interface BootstrapSyncUpdateResponse {
  data?: JsonResponse<"/v2/sync-updates/bootstrap", "get">;
  // TODO: any for now until codegen is fixed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
}
export interface BootstrapSyncUpdatesPaginatedResponse {
  data?: JsonResponse<"/v2/sync-updates/bootstrap/diff", "post">;
  // TODO: any for now until codgen is fixed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
}
export interface BootstrapGuestModeSyncUpdateResponse {
  // TODO - Update when guest mode support is implemented
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  // TODO: any for now until codgen is fixed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
}
export interface HydrationSyncUpdateResponse {
  data?: ApiSchema["HydrationSyncUpdatesResponseSchema"];
  // TODO: any for now until codgen is fixed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
}

export enum SyncQueueItemKind {
  SyncOperation = "SyncOperation",
  AcknowledgedSyncOperation = "AcknowledgedSyncOperation",
  OptimisticUpdate = "OptimisticUpdate",
}

export type SyncQueueItem =
  | { kind: SyncQueueItemKind.SyncOperation; data: SerializedSyncOperation }
  | { kind: SyncQueueItemKind.AcknowledgedSyncOperation; data: SerializedSyncOperation }
  | { kind: SyncQueueItemKind.OptimisticUpdate; data: OptimisticSyncUpdate<SyncModelData> };

export const ACL_SYNC_OPERATIONS: GenericSyncOperationKind[] = [
  "GRANT_NOTE_ACL_VIA_SPACE_ACCOUNT",
  "GRANT_NOTE_ACL_VIA_EMAIL_ADDRESS",
  "UPDATE_NOTE_ACL_VIA_SPACE_ACCOUNT",
  "UPDATE_NOTE_ACL_VIA_EMAIL_ADDRESS",
  "UPDATE_NOTE_ACL_VIA_COLLECTION",
  "REVOKE_NOTE_ACL_VIA_SPACE_ACCOUNT",
  "REVOKE_NOTE_ACL_VIA_EMAIL_ADDRESS",
  "GRANT_COLLECTION_ACL_VIA_SPACE_ACCOUNT",
  "GRANT_COLLECTION_ACL_VIA_EMAIL_ADDRESS",
  "UPDATE_COLLECTION_ACL_VIA_SPACE_ACCOUNT",
  "UPDATE_COLLECTION_ACL_VIA_EMAIL_ADDRESS",
  "REVOKE_COLLECTION_ACL_VIA_SPACE_ACCOUNT",
  "REVOKE_COLLECTION_ACL_VIA_EMAIL_ADDRESS",
];

export const ACL_SYNC_OPERATIONS_FOR_NOTES: GenericSyncOperationKind[] = [
  ...ACL_SYNC_OPERATIONS,
  "ADD_NOTE_TO_COLLECTION",
  "REMOVE_NOTE_FROM_COLLECTION",
];

export const TEMPLATE_ACL_SYNC_OPERATIONS_FOR_NOTES: GenericSyncOperationKind[] = [
  "CREATE_NOTE_USING_TEMPLATE_AND_DIFF",
  "UPDATE_NOTE_CONTENT_USING_TEMPLATE_AND_DIFF",
];

export const ACL_SYNC_OPERATIONS_FOR_TEMPLATES: GenericSyncOperationKind[] = [
  "GRANT_TEMPLATE_ACL_VIA_EMAIL_ADDRESS",
  "GRANT_TEMPLATE_ACL_VIA_SPACE_ACCOUNT",
  "UPDATE_TEMPLATE_ACL_VIA_SPACE_ACCOUNT",
  "UPDATE_TEMPLATE_ACL_VIA_COLLECTION",
  "REVOKE_TEMPLATE_ACL_VIA_SPACE_ACCOUNT",
  "GRANT_COLLECTION_ACL_VIA_SPACE_ACCOUNT",
  "GRANT_COLLECTION_ACL_VIA_EMAIL_ADDRESS",
  "UPDATE_COLLECTION_ACL_VIA_SPACE_ACCOUNT",
  "UPDATE_COLLECTION_ACL_VIA_EMAIL_ADDRESS",
  "REVOKE_COLLECTION_ACL_VIA_SPACE_ACCOUNT",
  "REVOKE_COLLECTION_ACL_VIA_EMAIL_ADDRESS",
  "ADD_TEMPLATE_TO_COLLECTION",
  "REMOVE_TEMPLATE_FROM_COLLECTION",
];

export enum QueueProcessingState {
  NotReady = "NotReady",
  Ready = "Ready",
  Processing = "Processing",
  Paused = "Paused",
}

export enum PreloadingState {
  NotStarted = "NotStarted",
  InProgress = "InProgress",
  Complete = "Complete",
  Failed = "Failed",
}

export type SyncCustomErrorData = ApiSchema["SubmitSyncOperationFailedCustomResponseData"];

export type SerializedOptimisticUpdate<ModelData extends SyncModelData> =
  OptimisticSyncUpdate<ModelData> & {
    sync_operation_id: Uuid;
    model_id: string;
    model_kind: SyncModelKindValues;
  };

export enum SyncModelRecomputePriority {
  High = 0, // Other models depend on this model
  Normal = 1, // This model depends on other models
  Low = 2, // This model does not depend on other models
  Skip = 9,
}

export const SYNC_OPERATION_EMAIL_NOT_FOUND_EXCEPTION = "SyncOperationEmailNotFoundException";
